import * as pc from 'playcanvas';
import { LEVEL_CONFIG } from '../config/levels.js';
import { flashBoss } from '../entities/boss.js';
import { handleBossCollision, getClosestPlayer, constrainToArenaBounds } from './collision.js';
import { playSound } from './audio.js';
import { screenShake } from './camera.js';
import { damagePlayer } from './combat.js';
import { createLaserChargeEffect, updateLaserChargeEffect, fireLaserBeam, updateLaserBeam, checkLaserCollision, destroyLaserBeam } from './laser.js';

// Boss AI state
let bossState = 'idle';
let bossTimer = 0;
let bossChargeTarget = null;

// Get current boss state
export function getBossState() {
    return { state: bossState, timer: bossTimer, chargeTarget: bossChargeTarget };
}

// Reset boss AI state
export function resetBossAI() {
    // Clean up any active laser
    destroyLaserBeam();
    bossState = 'idle';
    bossTimer = 0;
    bossChargeTarget = null;
}

// Update boss AI
export function updateBossAI(boss, players, collisionObjects, currentLevel, difficulty, dt) {
    if (!boss || boss.health <= 0) return;

    // Get closest living player
    const { player: targetPlayer, distance } = getClosestPlayer(boss, players);
    if (!targetPlayer) return;

    const bossPos = boss.getPosition();
    const playerPos = targetPlayer.getPosition();

    const dx = playerPos.x - bossPos.x;
    const dz = playerPos.z - bossPos.z;

    // Get level config
    const config = LEVEL_CONFIG[currentLevel];

    // Update boss attack cooldown
    if (boss.attackCooldown > 0) {
        boss.attackCooldown -= dt;
    }

    // State machine for boss behavior
    switch (bossState) {
        case 'idle':
            handleIdleState(boss, targetPlayer, distance, dx, dz, config, currentLevel, dt);
            break;

        case 'charging':
            handleChargingState(boss, config, dt);
            break;

        case 'attacking':
            handleAttackingState(boss, currentLevel, dt);
            break;

        case 'jumpWindup':
            handleJumpWindupState(boss, dt);
            break;

        case 'jumping':
            handleJumpingState(boss, players, distance, config, dt);
            break;

        case 'slamming':
            handleSlammingState(boss, dt);
            break;

        case 'recovering':
            handleRecoveringState(boss, config, dt);
            break;

        case 'spinWindup':
            handleSpinWindupState(boss, dt);
            break;

        case 'spinning':
            handleSpinningState(boss, players, distance, dx, dz, config, dt);
            break;

        case 'rageWindup':
            handleRageWindupState(boss, dt);
            break;

        case 'raging':
            handleRagingState(boss, players, playerPos, distance, dx, dz, config, dt);
            break;

        case 'laserCharge':
            handleLaserChargeState(boss, playerPos, dt);
            break;

        case 'laserFiring':
            handleLaserFiringState(boss, players, config, dt);
            break;

        case 'stunned':
            handleStunnedState(boss, dt);
            break;
    }

    // Face player (except when stunned)
    if (bossState !== 'stunned' && bossState !== 'spinning') {
        const angle = Math.atan2(dx, dz);
        boss.setEulerAngles(0, angle * (180 / Math.PI), 0);
    }

    // Handle collision with environment
    handleBossCollision(boss, collisionObjects);

    // Collision with players - push them away and damage if attacking
    for (const player of players) {
        if (player.health <= 0) continue;

        const pPos = player.getPosition();
        const pdx = pPos.x - bossPos.x;
        const pdz = pPos.z - bossPos.z;
        const pDist = Math.sqrt(pdx * pdx + pdz * pdz);

        if (pDist < 2) {
            const pushForce = (2 - pDist) * 10;
            const pushX = (pdx / pDist) * pushForce * dt;
            const pushZ = (pdz / pDist) * pushForce * dt;

            const constrained = constrainToArenaBounds(pPos.x + pushX, pPos.z + pushZ);
            player.setPosition(constrained.x, pPos.y, constrained.z);

            if (boss.isAttacking && !player.isInvincible) {
                damagePlayer(player, config.bossDamage);
            }
        }
    }
}

// Idle state - chase and decide on attack
function handleIdleState(boss, player, distance, dx, dz, config, level, dt) {
    // Chase player aggressively
    if (distance > 1.2) {
        const moveSpeed = boss.moveSpeed || config.bossSpeed;
        const dirX = dx / distance;
        const dirZ = dz / distance;
        // More aggressive chasing - faster when far, still fast when close
        const speedMultiplier = distance > 6 ? 1.5 : (distance > 3 ? 1.2 : 1.0);
        boss.setPosition(
            boss.getPosition().x + dirX * moveSpeed * speedMultiplier * dt,
            boss.baseY,
            boss.getPosition().z + dirZ * moveSpeed * speedMultiplier * dt
        );
    }

    // Start attack if close enough and cooldown ready
    const attackRange = 5 + level;
    if (distance < attackRange && boss.attackCooldown <= 0) {
        const attackType = Math.random();

        // All bosses have access to all attacks - higher levels use more advanced attacks more often
        const laserChance = 0.15 + level * 0.05;  // 20% at level 1, up to 40% at level 5
        const spinChance = 0.15 + level * 0.03;   // 18% at level 1, up to 30% at level 5
        const rageChance = level * 0.04;          // 4% at level 1, up to 20% at level 5
        const jumpChance = 0.25;                  // 25% constant
        // Remaining is charge attack

        if (attackType < laserChance) {
            // Laser beam - available on ALL levels
            bossState = 'laserCharge';
            bossTimer = 1.0 - level * 0.1; // Faster charge at higher levels
            bossChargeTarget = player.getPosition().clone();
            flashBoss(boss, new pc.Color(1, 0, 0), level);
            playSound('bossCharge');
            createLaserChargeEffect(boss, level);
        } else if (attackType < laserChance + spinChance) {
            // Spin attack
            bossState = 'spinWindup';
            bossTimer = config.telegraphTime * 0.6;
            flashBoss(boss, new pc.Color(0.5, 0, 1), level);
            playSound('bossCharge');
        } else if (attackType < laserChance + spinChance + rageChance) {
            // Rage mode
            bossState = 'rageWindup';
            bossTimer = config.telegraphTime * 0.5;
            flashBoss(boss, new pc.Color(1, 0, 0.5), level);
            playSound('bossCharge');
        } else if (attackType < laserChance + spinChance + rageChance + jumpChance) {
            // Jump slam
            bossState = 'jumpWindup';
            bossTimer = config.telegraphTime * 0.8;
            bossChargeTarget = player.getPosition().clone();
            flashBoss(boss, new pc.Color(1, 0.5, 0), level);
            playSound('bossCharge');
        } else {
            // Charge attack
            bossState = 'charging';
            bossTimer = config.telegraphTime;
            bossChargeTarget = player.getPosition().clone();
            flashBoss(boss, new pc.Color(1, 1, 0), level);
            playSound('bossCharge');
        }
    }
}

// Charging state - telegraph before dash
function handleChargingState(boss, config, dt) {
    bossTimer -= dt;
    boss.setLocalScale(
        boss.baseScale.x + Math.sin(bossTimer * 40) * 0.15,
        boss.baseScale.y,
        boss.baseScale.z
    );

    if (bossTimer <= 0) {
        bossState = 'attacking';
        bossTimer = 0.3;
        boss.isAttacking = true;
        playSound('bossAttack');
    }
}

// Attacking state - dash toward target
function handleAttackingState(boss, level, dt) {
    bossTimer -= dt;
    const chargeSpeed = 20 + level * 5;
    const bossPos = boss.getPosition();
    const chargeDx = bossChargeTarget.x - bossPos.x;
    const chargeDz = bossChargeTarget.z - bossPos.z;
    const chargeDist = Math.sqrt(chargeDx * chargeDx + chargeDz * chargeDz);

    if (chargeDist > 0.5) {
        boss.setPosition(
            bossPos.x + (chargeDx / chargeDist) * chargeSpeed * dt,
            boss.baseY,
            bossPos.z + (chargeDz / chargeDist) * chargeSpeed * dt
        );
    }

    if (bossTimer <= 0) {
        bossState = 'recovering';
        bossTimer = 0.3 - level * 0.04; // Faster recovery (was 0.5 - level * 0.1)
        boss.isAttacking = false;
        boss.setLocalScale(boss.baseScale.x, boss.baseScale.y, boss.baseScale.z);
    }
}

// Jump windup state
function handleJumpWindupState(boss, dt) {
    bossTimer -= dt;
    const crouchAmount = Math.sin((0.6 - bossTimer) * 5) * 0.3;
    boss.setLocalScale(
        boss.baseScale.x * (1 + crouchAmount * 0.2),
        boss.baseScale.y * (1 - crouchAmount),
        boss.baseScale.z * (1 + crouchAmount * 0.2)
    );

    if (bossTimer <= 0) {
        bossState = 'jumping';
        bossTimer = 0.8;
        boss.velocity.y = 18;
    }
}

// Jumping state
function handleJumpingState(boss, players, distance, config, dt) {
    bossTimer -= dt;
    boss.velocity.y -= 30 * dt;
    const bossPos = boss.getPosition();
    const jumpY = bossPos.y + boss.velocity.y * dt;

    if (jumpY <= boss.baseY && bossTimer <= 0) {
        boss.setPosition(bossPos.x, boss.baseY, bossPos.z);
        boss.velocity.y = 0;
        bossState = 'slamming';
        bossTimer = 0.2;
        boss.isAttacking = true;
        screenShake(0.3);
        playSound('bossAttack');

        // Damage nearby players
        for (const player of players) {
            const pPos = player.getPosition();
            const d = Math.sqrt(
                Math.pow(pPos.x - bossPos.x, 2) +
                Math.pow(pPos.z - bossPos.z, 2)
            );
            if (d < 4 && !player.isInvincible) {
                damagePlayer(player, config.bossDamage);
            }
        }
    } else {
        boss.setPosition(bossPos.x, jumpY, bossPos.z);
    }
}

// Slamming state
function handleSlammingState(boss, dt) {
    bossTimer -= dt;
    boss.setLocalScale(boss.baseScale.x * 1.3, boss.baseScale.y * 0.7, boss.baseScale.z * 1.3);

    if (bossTimer <= 0) {
        bossState = 'recovering';
        bossTimer = 0.5; // Faster recovery (was 0.8)
        boss.isAttacking = false;
    }
}

// Recovering state
function handleRecoveringState(boss, config, dt) {
    bossTimer -= dt;
    boss.setLocalScale(boss.baseScale.x, boss.baseScale.y, boss.baseScale.z);

    if (bossTimer <= 0) {
        bossState = 'idle';
        boss.attackCooldown = config.attackCooldown;
    }
}

// Spin windup state
function handleSpinWindupState(boss, dt) {
    bossTimer -= dt;
    boss.rotate(0, 100 * dt, 0);

    if (bossTimer <= 0) {
        bossState = 'spinning';
        bossTimer = 2.5; // Longer spin duration (was 1.5)
        boss.isAttacking = true;
        playSound('bossAttack');
    }
}

// Spinning state
function handleSpinningState(boss, players, distance, dx, dz, config, dt) {
    bossTimer -= dt;
    boss.rotate(0, 900 * dt, 0);

    // Move toward closest player
    if (distance > 0.5) {
        const spinSpeed = boss.moveSpeed * 2;
        const bossPos = boss.getPosition();
        boss.setPosition(
            bossPos.x + (dx / distance) * spinSpeed * dt,
            boss.baseY,
            bossPos.z + (dz / distance) * spinSpeed * dt
        );
    }

    // Damage on contact
    for (const player of players) {
        if (player.isInvincible || player.health <= 0) continue;
        const pPos = player.getPosition();
        const bPos = boss.getPosition();
        const d = Math.sqrt(Math.pow(pPos.x - bPos.x, 2) + Math.pow(pPos.z - bPos.z, 2));
        if (d < 2.5) {
            damagePlayer(player, config.bossDamage);
        }
    }

    if (bossTimer <= 0) {
        bossState = 'recovering';
        bossTimer = 0.4; // Faster recovery (was 0.6)
        boss.isAttacking = false;
    }
}

// Rage windup state
function handleRageWindupState(boss, dt) {
    bossTimer -= dt;
    const ragePulse = 1 + Math.sin(bossTimer * 30) * 0.1;
    boss.setLocalScale(
        boss.baseScale.x * ragePulse * 1.1,
        boss.baseScale.y * ragePulse * 1.1,
        boss.baseScale.z * ragePulse * 1.1
    );

    if (bossTimer <= 0) {
        bossState = 'raging';
        bossTimer = 3.0; // Longer rage duration (was 2.0)
        boss.rageAttacks = 0;
        boss.isAttacking = true;
        playSound('bossAttack');
    }
}

// Raging state
function handleRagingState(boss, players, playerPos, distance, dx, dz, config, dt) {
    bossTimer -= dt;

    if (bossTimer % 0.35 < dt && boss.rageAttacks < 8) {
        bossChargeTarget = playerPos.clone();
        boss.rageAttacks++;
        playSound('bossAttack');
    }

    // Chase aggressively
    if (distance > 0.5) {
        const rageSpeed = boss.moveSpeed * 2.5;
        const bossPos = boss.getPosition();
        boss.setPosition(
            bossPos.x + (dx / distance) * rageSpeed * dt,
            boss.baseY,
            bossPos.z + (dz / distance) * rageSpeed * dt
        );
    }

    // Damage on contact
    for (const player of players) {
        if (player.isInvincible || player.health <= 0) continue;
        const pPos = player.getPosition();
        const bPos = boss.getPosition();
        const d = Math.sqrt(Math.pow(pPos.x - bPos.x, 2) + Math.pow(pPos.z - bPos.z, 2));
        if (d < 2.5) {
            damagePlayer(player, config.bossDamage);
        }
    }

    // Pulse effect
    const rageScale = 1 + Math.sin(bossTimer * 15) * 0.05;
    boss.setLocalScale(
        boss.baseScale.x * rageScale,
        boss.baseScale.y * rageScale,
        boss.baseScale.z * rageScale
    );

    if (bossTimer <= 0) {
        bossState = 'recovering';
        bossTimer = 0.7; // Faster recovery (was 1.2)
        boss.isAttacking = false;
        boss.setLocalScale(boss.baseScale.x, boss.baseScale.y, boss.baseScale.z);
    }
}

// Laser charge state
function handleLaserChargeState(boss, playerPos, dt) {
    bossTimer -= dt;

    if (bossTimer > 0.3) {
        bossChargeTarget = playerPos.clone();
    }

    const chargeIntensity = 1 - bossTimer;
    updateLaserChargeEffect(chargeIntensity);

    if (bossTimer <= 0) {
        bossState = 'laserFiring';
        bossTimer = 0.8; // Longer laser duration (was 0.5)
        boss.isAttacking = true;
        fireLaserBeam(boss, bossChargeTarget, boss.level);
        playSound('bossAttack');
    }
}

// Laser firing state
function handleLaserFiringState(boss, players, config, dt) {
    bossTimer -= dt;
    updateLaserBeam();

    for (const player of players) {
        if (player.isInvincible || player.health <= 0) continue;
        if (checkLaserCollision(player)) {
            damagePlayer(player, config.bossDamage * 1.5);
        }
    }

    if (bossTimer <= 0) {
        bossState = 'recovering';
        bossTimer = 0.6; // Faster recovery (was 1.0)
        boss.isAttacking = false;
        destroyLaserBeam();
    }
}

// Stunned state
function handleStunnedState(boss, dt) {
    bossTimer -= dt;
    boss.setEulerAngles(
        Math.sin(bossTimer * 20) * 10,
        boss.getEulerAngles().y,
        Math.cos(bossTimer * 20) * 10
    );

    if (bossTimer <= 0) {
        bossState = 'idle';
        boss.setEulerAngles(0, boss.getEulerAngles().y, 0);
        boss.attackCooldown = 1;
    }
}

// Stun the boss
export function stunBoss(boss, duration = 0.5) {
    if (bossState !== 'stunned') {
        // Clean up laser if boss was firing
        if (bossState === 'laserFiring' || bossState === 'laserCharge') {
            destroyLaserBeam();
        }
        bossState = 'stunned';
        bossTimer = duration;
        boss.isAttacking = false;
    }
}

// Set boss state directly (for testing/debugging)
export function setBossState(state, timer = 0) {
    bossState = state;
    bossTimer = timer;
}
